import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { billOperatorsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Zap, Plus, Pencil, Trash2, Loader2, Save } from 'lucide-react';

const SERVICE_TYPES = [
  { value: 'electricity', label: 'Electricity' },
  { value: 'gas', label: 'Gas' },
  { value: 'piped_gas', label: 'Piped Gas' },
  { value: 'water', label: 'Water' },
  { value: 'broadband', label: 'Broadband' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'cylinder', label: 'Book a Cylinder' },
  { value: 'fastag', label: 'FASTag' },
  { value: 'loan', label: 'Loan' },
];

export default function BillOperators() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    service_type: 'electricity', operator_name: '', operator_code: '',
    has_field1: 0, field1_label: '', field1_options: '',
    has_field2: 0, field2_label: '', field2_options: '',
    status: 'active',
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const r = await billOperatorsAPI.list();
      setItems(r.data || []);
    } catch {} finally { setLoading(false); }
  };

  const reset = () => {
    setForm({
      service_type: 'electricity', operator_name: '', operator_code: '',
      has_field1: 0, field1_label: '', field1_options: '',
      has_field2: 0, field2_label: '', field2_options: '',
      status: 'active',
    });
    setEditId(null);
  };

  const openEdit = (item) => {
    const f1Opts = item.field1_options
      ? (typeof item.field1_options === 'string' ? item.field1_options : JSON.stringify(item.field1_options))
      : '';
    const f2Opts = item.field2_options
      ? (typeof item.field2_options === 'string' ? item.field2_options : JSON.stringify(item.field2_options))
      : '';
    setForm({
      service_type: item.service_type,
      operator_name: item.operator_name,
      operator_code: item.operator_code,
      has_field1: item.has_field1 ? 1 : 0,
      field1_label: item.field1_label || '',
      field1_options: f1Opts,
      has_field2: item.has_field2 ? 1 : 0,
      field2_label: item.field2_label || '',
      field2_options: f2Opts,
      status: item.status,
    });
    setEditId(item.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.operator_name || !form.operator_code) {
      toast.error('Operator name and code are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Validate JSON if field options provided
      if (payload.has_field1 && payload.field1_options) {
        try { JSON.parse(payload.field1_options); } catch { toast.error('Field 1 options must be valid JSON'); setSaving(false); return; }
      }
      if (payload.has_field2 && payload.field2_options) {
        try { JSON.parse(payload.field2_options); } catch { toast.error('Field 2 options must be valid JSON'); setSaving(false); return; }
      }
      if (editId) await billOperatorsAPI.update(editId, payload);
      else await billOperatorsAPI.create(payload);
      toast.success(editId ? 'Updated' : 'Added');
      fetchAll();
      setDialogOpen(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this operator?')) return;
    try {
      await billOperatorsAPI.delete(id);
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const getF1Count = (item) => {
    if (!item.field1_options) return 0;
    const opts = typeof item.field1_options === 'string' ? JSON.parse(item.field1_options) : item.field1_options;
    return Array.isArray(opts) ? opts.length : 0;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="bill-operators-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Zap className="w-6 h-6" style={{ color: '#eab308' }} />
              Bill Operators
            </h1>
            <p className="text-sm text-slate-500">Manage bill payment service providers</p>
          </div>
          <Button onClick={() => { reset(); setDialogOpen(true); }} style={{ background: '#1e1b4b' }} className="text-white" data-testid="add-bill-op-btn">
            <Plus className="w-4 h-4 mr-2" />Add Operator
          </Button>
        </div>

        <div className="bg-white rounded-xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Service</TableHead>
                <TableHead>Operator</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Fields</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1, 2, 3].map(i => (
                  <TableRow key={i}><TableCell colSpan={6}><div className="h-8 bg-slate-100 rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">No bill operators added</TableCell></TableRow>
              ) : (
                items.map(item => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Badge variant="outline" className="capitalize text-xs">{item.service_type}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{item.operator_name}</TableCell>
                    <TableCell><code className="text-xs bg-slate-100 px-2 py-1 rounded">{item.operator_code}</code></TableCell>
                    <TableCell>
                      {item.has_field1 ? (
                        <span className="text-xs text-slate-500">{item.field1_label} ({getF1Count(item)} opts)</span>
                      ) : <span className="text-xs text-slate-300">None</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={item.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}>
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`edit-${item.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-${item.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="bill-op-dialog">
            <DialogHeader>
              <DialogTitle>{editId ? 'Edit' : 'Add'} Bill Operator</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Service Type</label>
                  <Select value={form.service_type} onValueChange={v => setForm({ ...form, service_type: v })}>
                    <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Status</label>
                  <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                    <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Operator Name</label>
                <Input value={form.operator_name} onChange={e => setForm({ ...form, operator_name: e.target.value })} placeholder="e.g. West Bengal State Electricity" className="h-10" data-testid="bill-op-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Operator Code (API)</label>
                <Input value={form.operator_code} onChange={e => setForm({ ...form, operator_code: e.target.value })} placeholder="e.g. WBSE" className="h-10" data-testid="bill-op-code" />
              </div>

              {/* Field 1 */}
              <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.has_field1 === 1} onChange={e => setForm({ ...form, has_field1: e.target.checked ? 1 : 0 })} />
                  <span className="text-xs font-medium text-slate-600">Has Field 1 (e.g., Sub Division)</span>
                </label>
                {form.has_field1 === 1 && (
                  <>
                    <Input value={form.field1_label} onChange={e => setForm({ ...form, field1_label: e.target.value })} placeholder="Label (e.g. Sub Division)" className="h-9 text-sm" />
                    <textarea value={form.field1_options} onChange={e => setForm({ ...form, field1_options: e.target.value })} placeholder='JSON array: [{"code":"001","name":"Option 1"}]' className="w-full h-20 text-xs p-2 border rounded-lg font-mono resize-none" />
                  </>
                )}
              </div>

              {/* Field 2 */}
              <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.has_field2 === 1} onChange={e => setForm({ ...form, has_field2: e.target.checked ? 1 : 0 })} />
                  <span className="text-xs font-medium text-slate-600">Has Field 2</span>
                </label>
                {form.has_field2 === 1 && (
                  <>
                    <Input value={form.field2_label} onChange={e => setForm({ ...form, field2_label: e.target.value })} placeholder="Label" className="h-9 text-sm" />
                    <textarea value={form.field2_options} onChange={e => setForm({ ...form, field2_options: e.target.value })} placeholder='JSON array: [{"code":"001","name":"Option 1"}]' className="w-full h-20 text-xs p-2 border rounded-lg font-mono resize-none" />
                  </>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} style={{ background: '#1e1b4b' }} className="text-white" data-testid="save-bill-op-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editId ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
